import { useEffect, useCallback } from 'react';
import { useMapControls } from '../context/MapControlsContext';

/**
 * Custom hook for managing individual control minimization state
 * @param {string} controlId - Unique identifier for the control
 * @param {boolean} defaultMinimized - Default minimized state
 * @returns {object} Control state and methods
 */
export const useMinimization = (controlId, defaultMinimized = false) => {
  const {
    toggleMinimized,
    minimize,
    expand,
    isMinimized,
    registerControl,
    unregisterControl,
  } = useMapControls();

  // Register control on mount and unregister on unmount
  useEffect(() => {
    registerControl(controlId);
    if (defaultMinimized) {
      minimize(controlId);
    }
    
    return () => {
      unregisterControl(controlId);
    };
  }, [controlId, defaultMinimized, registerControl, unregisterControl, minimize]);

  const toggle = useCallback(() => {
    toggleMinimized(controlId);
  }, [controlId, toggleMinimized]);

  const minimizeControl = useCallback(() => {
    minimize(controlId);
  }, [controlId, minimize]);

  const expandControl = useCallback(() => {
    expand(controlId);
  }, [controlId, expand]);

  const minimized = isMinimized(controlId);

  return {
    minimized,
    toggle,
    minimize: minimizeControl,
    expand: expandControl,
  };
};